import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { removeEmptyValues } from '@common';
import { COUNTRY_ISO_3_CODE, CountryIso3Code } from '@i18n';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddressSearchResultOverview } from '../../models/address-search-result/address-search-result-overview.interface';
import { AddressSearchResult } from '../../models/address-search-result/address-search-result.interface';
import { FormAddress } from '../../models/form-address/form-address.interface';
import {
  formatAddressSearchResultToFormAddress,
  formatFormAddressToCustomerAddress,
} from '../../utils/address.utils';

@Injectable({ providedIn: 'root' })
export class AddressService {
  readonly searchAddressApi =
    'https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws';
  readonly getAddressByIdApi =
    'https://api.addressy.com/Capture/Interactive/Retrieve/v1.1/json3.ws';
  readonly key = 'ZG99-WD62-FP46-DC55';

  private readonly httpClient = inject(HttpClient);
  private readonly countryIso3Code = inject(COUNTRY_ISO_3_CODE);
  private readonly localeId = inject(LOCALE_ID);

  // === Private Signals ===
  private readonly _selectedAddress = signal<FormAddress | undefined>(
    undefined,
  );
  private readonly _isLoading = signal<boolean>(false);
  private readonly _searchResults = signal<AddressSearchResultOverview[]>([]);

  // === Public Readonly Signals ===
  readonly selectedAddress = computed(() => {
    const value = this._selectedAddress();
    return value as FormAddress;
  });

  readonly customerAddress = computed(() => {
    const value = this._selectedAddress();
    return formatFormAddressToCustomerAddress(value as FormAddress);
  });

  readonly isLoading = this._isLoading.asReadonly();
  readonly searchResults = this._searchResults.asReadonly();

  // === Locale-aware labels ===
  readonly isIreland = computed(
    () => this.countryIso3Code === CountryIso3Code.Ireland,
  );
  readonly postcodeLabel = computed(() =>
    this.isIreland() ? 'Eircode' : 'Postcode',
  );
  readonly searchPlaceholder = computed(() =>
    this.isIreland()
      ? 'Start typing your address or Eircode'
      : 'Start typing your address or postcode',
  );

  // === Mutation methods ===
  setSelectedAddress(address: FormAddress | undefined): void {
    this._selectedAddress.set(address);
  }

  clearSearchResults(): void {
    this._searchResults.set([]);
  }

  //  === API calls ===
  searchAddress(
    searchTerm?: string,
    containerId = '',
  ): Observable<AddressSearchResultOverview[]> {
    if (!searchTerm || searchTerm.length < 3) {
      this._searchResults.set([]);
      return of([]);
    }

    this._isLoading.set(true);

    return this.httpClient
      .get<{ Items: AddressSearchResultOverview[] }>(this.searchAddressApi, {
        params: {
          IsMiddleware: 'false',
          Limit: '',
          Container: containerId,
          Countries: this.countryIso3Code,
          Key: this.key,
          Language: this.localeId,
          Origin: this.countryIso3Code,
          Text: searchTerm,
        },
      })
      .pipe(
        map((response) => {
          this._isLoading.set(false);
          this._searchResults.set(response.Items || []);
          return response.Items || [];
        }),
      );
  }

  getAddressById(id: string): Observable<AddressSearchResult> {
    return this.httpClient
      .get<{ Items: AddressSearchResult[] }>(this.getAddressByIdApi, {
        params: {
          Id: id,
          Key: this.key,
        },
      })
      .pipe(map((result: { Items: AddressSearchResult[] }) => result.Items[0]));
  }

  getFormAddressById(id: string): Observable<Partial<FormAddress>> {
    this._isLoading.set(true);

    return this.getAddressById(id).pipe(
      map((result: AddressSearchResult) => {
        this._isLoading.set(false);
        return removeEmptyValues(
          formatAddressSearchResultToFormAddress(result),
        );
      }),
    );
  }
}
